/*
  Shape of a system drill-down (Aurora, Solo, One Portal).

  The screens are one layout driven by data: a scope row, the health tiles, a
  period selector over the sales tiles, the quarterly roll-up beside the sales
  timeline, and the downtime log. Only the numbers and the copy differ per
  system, so each page passes a `SystemPageConfig` and `SystemIntelligenceView`
  fetches the rest.

  These are the *view* shapes. The wire shapes live in `api/deep-dive.types.ts`
  and are translated across in `deep-dive-adapters.ts` — so a field the contract
  renames is a compile error in one file, not a silent blank on three screens.
*/

import type { DeepDiveService, SalesFilterKey, SalesPeriodKey } from './api/deep-dive.types'

/* -------------------------------------------------------------- periods */

/**
 * The periods the API accepts, in the order they read.
 *
 * One list for the picker's options and the sales tile's title: a tile headed
 * "Sales This Week" over a figure the picker calls something else is the kind of
 * drift that gets a number quoted for the wrong span.
 */
export const PERIOD_KEYS: SalesPeriodKey[] = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_year',
  'last_year',
  'custom',
]

export const PERIOD_LABELS: Record<SalesPeriodKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This Week',
  last_week: 'Last Week',
  this_month: 'This Month',
  last_month: 'Last Month',
  this_year: 'This Year',
  last_year: 'Last Year',
  custom: 'Custom',
}

/* ------------------------------------------------------------ page identity */

export interface SystemPageConfig {
  /** Path segment on the API, e.g. `one-portal`. */
  service: DeepDiveService
  /** Page heading, e.g. "Solo — Sales Submission". */
  title: string
  /** What one row of the sales figures counts: "Sales", "Submissions". */
  unit: string
  /**
   * Tighten every band so the whole screen fits a desktop viewport without the
   * content column scrolling. Solo asks for this; Aurora keeps the roomier
   * spacing. Adding a panel or a table row will bring the scrollbar back on a
   * compact page — measure, don't eyeball.
   */
  compact?: boolean
  /**
   * Three tiles under the scope row — response time, 30-day uptime and the
   * chosen period's sales — and none above it.
   *
   * The scope row moves above the tiles, and the period summary row (sales,
   * cancelled, kW, contract value) is dropped: the sales tile carries the
   * period's figure and follows the selector, so "This Week" retitles it and
   * shows the week.
   *
   * All three sales screens ask for this today. The flag stays because the
   * alternative — health tiles above the scope row, four period tiles below it
   * — is still the layout a new system page would start from, and it is one
   * line to go back to.
   */
  salesTodayHeader?: boolean
}

/* ------------------------------------------------------------------ filters */

/**
 * The option meaning "don't send this filter at all".
 *
 * A sentinel rather than an empty string so it can be displayed in the chip, and
 * distinct enough that no real upstream value ("AZ :: Arizona", "UNTD") collides
 * with it.
 */
export const ALL_OPTION = 'All'

export interface FilterGroup {
  /** The query parameter this dimension is sent as. */
  id: SalesFilterKey
  /** Prefix shown before the chosen value, e.g. "STATE:". */
  label: string
  /** `ALL_OPTION` first, then the upstream values verbatim — never trimmed. */
  options: string[]
  /** Upstream's own caveat, e.g. region's "…not geography, use state or office". */
  note?: string
}

/* --------------------------------------------------------------- stat tiles */

export interface StatTileData {
  id: string
  label: string
  /** Already formatted for display — "13ms", "100%", or "n/a" when unknown. */
  value: string
  /** Secondary text beside the value, e.g. "/ Exp. 12-17", "92.8% observed". */
  note?: string
  tone: 'healthy' | 'degraded' | 'critical' | 'neutral'
  /**
   * Incidents recorded in the window — always red, since a count is only worth
   * surfacing when someone should look at it. Omitted when there is nothing to
   * count (or the count could not be fetched).
   */
  delta?: { value: string; direction: 'up' | 'down'; title: string }
}

/* ------------------------------------------------------ quarterly performance */

export interface QuarterPoint {
  quarter: string
  /** Deals recorded in the quarter. Future quarters come back as zero. */
  sales: number
  /** Cancellations in the same quarter — high enough here to be worth showing. */
  cancelled: number
  kw: number
  /** null on One Portal: their API carries no revenue. */
  contractValue: number | null
}

/* ----------------------------------------------------------- sales timeline */

export interface TimelinePoint {
  /**
   * What this point is called on the axis and in the readout — "14:00" for an
   * hour of one day, "Tue 8" for a day of a week.
   *
   * The point carries its own label rather than the chart deriving one, because
   * the same chart now plots both: a single day hour by hour, and a 7-day window
   * day by day. A chart that assumed hours would silently mislabel the second.
   */
  label: string
  /**
   * null means **unknown**, never zero: a bucket from before the source had any
   * data, or one still in the future. The chart lifts the pen for it rather than
   * dropping to a floor nobody measured.
   */
  actual: number | null
  baseline: number
  /** The plotted series: running totals, so the two curves diverge visibly. */
  cumulativeActual: number | null
  cumulativeBaseline: number
  /** True on the bucket that straddles the start of coverage. */
  partial?: boolean
}

/* --------------------------------------------------------------- incidents */

/**
 * Status vocabulary of an incident transition, kept at the server's six values
 * rather than folded into the dashboard's four. The dots are the whole point of
 * the row, and collapsing `maintenance` into `degraded` would draw a planned
 * window as a fault.
 */
export type IncidentStatus =
  'healthy' | 'minor' | 'major' | 'critical' | 'maintenance' | 'vendor_silent'

/** Vendor indicator code as it arrives on the feed. */
export type IncidentIndicator =
  'CRITICAL_FAIL' | 'DEGRADED' | 'MINOR' | 'MAINTENANCE' | 'VENDOR_SILENT' | 'LATENCY_HIGH'

export interface Incident {
  id: string
  /** Start of the transition, formatted for display. */
  date: string
  from: IncidentStatus
  to: IncidentStatus
  /** "18m 04s", or "Ongoing" while it is still open. */
  duration: string
  indicator: IncidentIndicator
  ongoing: boolean
}

/* --------------------------------------------------------------- freshness */

/** The stale/as-of line shown under a panel that is fed from OWEHub. */
export interface Freshness {
  /** Max modified time of the matched data; null when nothing matched. */
  dataAsOf: string | null
  /** False = the upstream sync is failing, so the figures may be stale. */
  syncHealthy: boolean
}
